// Rental-distribution endpoint functions.
//
// Accept camelCase view-model inputs, return camelCase view models. Throw
// AppError (the httpClient interceptor normalises) — never wrap in Result<T>.

import { adminApi } from '@core/api/httpClient'
import type { PaginatedResponse } from '@shared/types/pagination'
import { RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS } from './rentalDistributionsEndpointPaths'
import type {
  RentalDistributionCountsDto,
  RentalDistributionDetailDto,
  RentalDistributionsListDto,
  DeclaredRentalDistributionDto,
  EligiblePropertyDto,
  RentalDistributionPreviewDto,
  RentalPayoutsListDto,
  RunApprovalPermissionDto,
  RunPaymentPermissionDto,
  RunReversalPermissionDto,
} from './rentalDistributionsContracts'
import {
  parseRentalDistributionCounts,
  parseRentalDistributionDetail,
  parseRentalDistributionPeriods,
  parseRentalDistributionsList,
  parseEligibleProperty,
  parseRentalDistributionPreview,
  parseRentalPayoutsList,
  parseRunApprovalPermission,
  parseRunPaymentPermission,
  parseRunReversalPermission,
} from './rentalDistributionsParser'
import type {
  ApproveRentalDistributionInput,
  RentalDistribution,
  RentalDistributionCounts,
  RentalDistributionDetail,
  RentalDistributionFilters,
  RentalPayout,
  RentalPayoutFilters,
  DeclareRentalDistributionInput,
  EligibleProperty,
  RentalDistributionPreview,
  ProcessRunPayoutsInput,
  RetryFailedPayoutsInput,
  RejectRentalDistributionInput,
  ReverseRentalDistributionInput,
  RunApprovalPermission,
  RunPaymentPermission,
  RunReversalPermission,
} from '../types/rentalDistributionTypes'

/**
 * Every admin-service response is wrapped by its global transform interceptor
 * as `{ success, statusCode, message, data }`. Strip that one level here so no
 * parser ever sees the transport envelope — including list responses, whose
 * payload is itself `{ data, meta }`.
 */
function unwrap<T>(payload: T | { data: T }): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

function toQueryParams(filters: Record<string, unknown>): Record<string, string> {
  const params: Record<string, string> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue
    params[key] = String(value)
  }
  return params
}

// ─── Reads ──────────────────────────────────────────────────────────────────

export async function getRentalDistributions(
  filters: RentalDistributionFilters,
): Promise<PaginatedResponse<RentalDistribution>> {
  const { data } = await adminApi.get<
    RentalDistributionsListDto | { data: RentalDistributionsListDto }
  >(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.LIST, { params: toQueryParams({ ...filters }) })
  return parseRentalDistributionsList(unwrap(data))
}

export async function getRentalDistributionCounts(): Promise<RentalDistributionCounts> {
  const { data } = await adminApi.get<
    RentalDistributionCountsDto | { data: RentalDistributionCountsDto }
  >(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.STATUS_COUNTS)
  return parseRentalDistributionCounts(unwrap(data))
}

export async function getRentalDistributionPeriods(): Promise<string[]> {
  const { data } = await adminApi.get<string[] | { data: string[] }>(
    RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.PERIODS,
  )
  return parseRentalDistributionPeriods(unwrap(data))
}

export async function getRentalDistribution(
  distributionId: string,
): Promise<RentalDistributionDetail> {
  const { data } = await adminApi.get<
    RentalDistributionDetailDto | { data: RentalDistributionDetailDto }
  >(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.DETAIL(distributionId))
  return parseRentalDistributionDetail(unwrap(data))
}

export async function getRentalPayouts(
  distributionId: string,
  filters: RentalPayoutFilters,
): Promise<PaginatedResponse<RentalPayout>> {
  const { data } = await adminApi.get<RentalPayoutsListDto | { data: RentalPayoutsListDto }>(
    RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.PAYOUTS(distributionId),
    { params: toQueryParams({ ...filters }) },
  )
  return parseRentalPayoutsList(unwrap(data))
}

// ─── Writes (run level only) ────────────────────────────────────────────────

export async function approveRentalDistribution(
  input: ApproveRentalDistributionInput,
): Promise<RentalDistributionDetail> {
  const { distributionId, ...payload } = input
  const { data } = await adminApi.post<
    RentalDistributionDetailDto | { data: RentalDistributionDetailDto }
  >(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.APPROVE(distributionId), payload)
  return parseRentalDistributionDetail(unwrap(data))
}

export async function retryFailedPayouts(
  input: RetryFailedPayoutsInput,
): Promise<RentalDistributionDetail> {
  const { distributionId, ...payload } = input
  const { data } = await adminApi.post<
    RentalDistributionDetailDto | { data: RentalDistributionDetailDto }
  >(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.RETRY_FAILED(distributionId), payload)
  return parseRentalDistributionDetail(unwrap(data))
}

/**
 * Whether the signed-in admin may process this run's queued payouts, and why
 * not. Read separately from the run for the same reason as approval
 * permission — the answer depends on WHO is asking.
 */
export async function getRunPaymentPermission(
  distributionId: string,
): Promise<RunPaymentPermission> {
  const { data } = await adminApi.get<RunPaymentPermissionDto | { data: RunPaymentPermissionDto }>(
    RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.PAYMENT_PERMISSION(distributionId),
  )
  return parseRunPaymentPermission(unwrap(data))
}

/** Pays every queued payout in a run — the deliberate second step after approval. */
export async function processRunPayouts(
  input: ProcessRunPayoutsInput,
): Promise<RentalDistributionDetail> {
  const { distributionId, ...payload } = input
  const { data } = await adminApi.post<
    RentalDistributionDetailDto | { data: RentalDistributionDetailDto }
  >(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.PROCESS_PAYOUTS(distributionId), payload)
  return parseRentalDistributionDetail(unwrap(data))
}

// ─── Declaration ────────────────────────────────────────────────────────────

export async function getEligibleProperties(periodKey: string): Promise<EligibleProperty[]> {
  const { data } = await adminApi.get<EligiblePropertyDto[] | { data: EligiblePropertyDto[] }>(
    RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.ELIGIBLE_PROPERTIES,
    { params: { periodKey } },
  )
  return unwrap(data).map(parseEligibleProperty)
}

/** Computes a distribution without writing it — safe to call as the form changes. */
export async function previewRentalDistribution(
  input: DeclareRentalDistributionInput,
): Promise<RentalDistributionPreview> {
  const { data } = await adminApi.post<
    RentalDistributionPreviewDto | { data: RentalDistributionPreviewDto }
  >(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.PREVIEW, input)
  return parseRentalDistributionPreview(unwrap(data))
}

export async function declareRentalDistribution(
  input: DeclareRentalDistributionInput,
): Promise<string> {
  const { data } = await adminApi.post<
    DeclaredRentalDistributionDto | { data: DeclaredRentalDistributionDto }
  >(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.DECLARE, input)
  return unwrap(data).distributionId
}

// ─── Lifecycle ──────────────────────────────────────────────────────────────

/** Hands a calculated run to a checker. Its figures are frozen from here. */
export async function submitRunForApproval(distributionId: string): Promise<void> {
  await adminApi.post(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.SUBMIT_FOR_APPROVAL(distributionId))
}

/**
 * Whether the signed-in admin may approve this run, and why not.
 *
 * Read separately from the run itself because it depends on WHO is asking —
 * caching it on the distribution detail would serve one admin's answer to
 * another.
 */
export async function getRunApprovalPermission(
  distributionId: string,
): Promise<RunApprovalPermission> {
  const { data } = await adminApi.get<
    RunApprovalPermissionDto | { data: RunApprovalPermissionDto }
  >(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.APPROVAL_PERMISSION(distributionId))
  return parseRunApprovalPermission(unwrap(data))
}

export async function rejectRentalDistribution(
  input: RejectRentalDistributionInput,
): Promise<void> {
  await adminApi.post(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.REJECT(input.distributionId), {
    reason: input.reason,
  })
}

/**
 * Whether the signed-in admin may reverse this run, and why not.
 *
 * Read separately from the run itself for the same reason as the other two
 * permission queries — the answer depends on WHO is asking.
 */
export async function getRunReversalPermission(
  distributionId: string,
): Promise<RunReversalPermission> {
  const { data } = await adminApi.get<
    RunReversalPermissionDto | { data: RunReversalPermissionDto }
  >(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.REVERSAL_PERMISSION(distributionId))
  return parseRunReversalPermission(unwrap(data))
}

/** Takes a paid-out run back out of investor wallets. Reason is mandatory. */
export async function reverseRentalDistribution(
  input: ReverseRentalDistributionInput,
): Promise<void> {
  await adminApi.post(RENTAL_DISTRIBUTIONS_ENDPOINT_PATHS.REVERSE(input.distributionId), {
    reason: input.reason,
  })
}
